// @seventide/types — care module DTOs (FR-5.1 – FR-5.11, spec §7.2 projection, §11 Money › Care).
// Owned by the care module (docs/ARCHITECTURE.md §6). Money is integer pence, dates ISO `yyyy-mm-dd`.
import { z } from 'zod';
import { Id, IsoDate, Pence, PositivePence } from './common';
import { DeadlineStatus } from './enums';

// ---------------------------------------------------------------- shared enums

/** Means-test setting used by the rules engine (`CareSetting` in @seventide/rules). */
export const CARE_MEANS_SETTINGS = ['residential', 'nursing', 'home'] as const;
export const CareMeansSetting = z.enum(CARE_MEANS_SETTINGS);
export type CareMeansSetting = z.infer<typeof CareMeansSetting>;
export const CARE_MEANS_SETTING_LABELS: Record<CareMeansSetting, string> = {
  residential: 'Care home (residential)',
  nursing: 'Nursing home',
  home: 'Care at home',
};

/** FR-5.1 main-home disregard status chosen by the user. `none` = no one qualifying lives there. */
export const CARE_HOME_DISREGARDS = [
  'none',
  'spouse_partner',
  'relative_over_60',
  'disabled_relative',
  'child_under_18',
] as const;
export const CareHomeDisregard = z.enum(CARE_HOME_DISREGARDS);
export type CareHomeDisregard = z.infer<typeof CareHomeDisregard>;
export const CARE_HOME_DISREGARD_LABELS: Record<CareHomeDisregard, string> = {
  none: 'No one who qualifies lives there',
  spouse_partner: 'A spouse or partner still lives there',
  relative_over_60: 'A relative aged 60 or over lives there',
  disabled_relative: 'A disabled relative lives there',
  child_under_18: 'A child under 18 lives there',
};

/** Year-month `YYYY-MM`. */
export const CareYearMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use the format yyyy-mm');
export type CareYearMonth = z.infer<typeof CareYearMonth>;

const optionalText = (max: number) => z.string().trim().max(max).nullish();

/** One line of an engine explanation trail (`Explanation` in @seventide/rules). */
export const CareExplanation = z.object({
  ruleId: z.string(),
  text: z.string(),
  amountPence: z.number().int().optional(),
});
export type CareExplanation = z.infer<typeof CareExplanation>;

// ---------------------------------------------------------------- care profile (FR-5.1, FR-5.2, FR-5.6)

export const CareProfileDto = z.object({
  id: Id.nullable(),
  setting: CareMeansSetting,
  /** In permanent residential or nursing care (starts the 12-week property disregard). */
  permanentCare: z.boolean(),
  admissionDate: IsoDate.nullable(),
  homeOwned: z.boolean(),
  /** Money account (type property) that holds the home's value, if linked. */
  homeAccountId: Id.nullable(),
  homeValuePence: PositivePence.nullable(),
  homeValueAsOf: IsoDate.nullable(),
  homeSecuredDebtPence: PositivePence,
  homeDisregard: CareHomeDisregard,
  homeOnMarket: z.boolean(),
  homeSaleDate: IsoDate.nullable(),
  /** Minimum Income Guarantee key (care at home); null = the rule set's default. */
  migKey: z.string().nullable(),
  migPremiumKeys: z.array(z.string()),
  /** FR-5.6 lead times in months before the projected upper-threshold month. */
  alertLeadMonths: z.array(z.number().int()),
  alertOnCrossing: z.boolean(),
  updatedAt: z.string().nullable(),
});
export type CareProfileDto = z.infer<typeof CareProfileDto>;

export const CareAlertLeadMonths = z
  .array(z.number().int().min(1, 'Use at least 1 month').max(24, 'Use 24 months or fewer'))
  .max(6, 'Choose up to 6 reminders')
  .refine((a) => new Set(a).size === a.length, 'Each reminder must be different');

export const UpdateCareProfileRequest = z
  .object({
    setting: CareMeansSetting,
    permanentCare: z.boolean(),
    admissionDate: IsoDate.nullable(),
    homeOwned: z.boolean(),
    homeAccountId: Id.nullable(),
    homeValuePence: PositivePence.nullable(),
    homeValueAsOf: IsoDate.nullable(),
    homeSecuredDebtPence: PositivePence,
    homeDisregard: CareHomeDisregard,
    homeOnMarket: z.boolean(),
    homeSaleDate: IsoDate.nullable(),
    migKey: z.string().max(60).nullable(),
    migPremiumKeys: z.array(z.string().max(60)).max(10),
    alertLeadMonths: CareAlertLeadMonths,
    alertOnCrossing: z.boolean(),
  })
  .partial();
export type UpdateCareProfileRequest = z.infer<typeof UpdateCareProfileRequest>;

// ---------------------------------------------------------------- capital items (FR-5.1)

/** Mirrors `CapitalAssetKind` in @seventide/rules. */
export const CARE_CAPITAL_ITEM_KINDS = [
  'cash',
  'savings',
  'investments',
  'other_property',
  'personal_possessions',
  'life_policy_excluded',
  'compensation_excluded',
  'other',
] as const;
export const CareCapitalItemKind = z.enum(CARE_CAPITAL_ITEM_KINDS);
export type CareCapitalItemKind = z.infer<typeof CareCapitalItemKind>;
export const CARE_CAPITAL_ITEM_KIND_LABELS: Record<CareCapitalItemKind, string> = {
  cash: 'Cash',
  savings: 'Savings',
  investments: 'Investments',
  other_property: 'Other property or land',
  personal_possessions: 'Personal possessions',
  life_policy_excluded: 'Life insurance policy (surrender value not counted)',
  compensation_excluded: 'Compensation payment that is not counted',
  other: 'Something else',
};

export const CareCapitalItemDto = z.object({
  id: Id,
  kind: CareCapitalItemKind,
  description: z.string(),
  valuePence: PositivePence,
  asOf: IsoDate.nullable(),
  notes: z.string().nullable(),
});
export type CareCapitalItemDto = z.infer<typeof CareCapitalItemDto>;

export const CreateCareCapitalItemRequest = z.object({
  kind: CareCapitalItemKind,
  description: z.string().trim().min(1, 'Describe the item').max(200),
  valuePence: PositivePence,
  asOf: IsoDate.nullish(),
  notes: optionalText(2000),
});
export type CreateCareCapitalItemRequest = z.infer<typeof CreateCareCapitalItemRequest>;
export const UpdateCareCapitalItemRequest = CreateCareCapitalItemRequest.partial();
export type UpdateCareCapitalItemRequest = z.infer<typeof UpdateCareCapitalItemRequest>;

/** Groups used on the capital breakdown (FR-5.1). */
export const CARE_CAPITAL_GROUPS = ['cash_savings', 'investments', 'home', 'other_property', 'other', 'excluded'] as const;
export const CareCapitalGroup = z.enum(CARE_CAPITAL_GROUPS);
export type CareCapitalGroup = z.infer<typeof CareCapitalGroup>;
export const CARE_CAPITAL_GROUP_LABELS: Record<CareCapitalGroup, string> = {
  cash_savings: 'Cash and savings',
  investments: 'Investments',
  home: 'Main home',
  other_property: 'Other property',
  other: 'Other capital',
  excluded: 'Not counted',
};

export const CareCapitalLine = z.object({
  key: z.string(),
  source: z.enum(['account', 'manual', 'home', 'snapshot']),
  sourceId: Id.nullable(),
  description: z.string(),
  group: CareCapitalGroup,
  valuePence: Pence,
  countedPence: Pence,
  /** Date of the balance/valuation used. */
  asOf: IsoDate.nullable(),
  counted: z.boolean(),
  /** Why it is not counted, or how it is counted (plain English). */
  reason: z.string().nullable(),
  ruleId: z.string(),
});
export type CareCapitalLine = z.infer<typeof CareCapitalLine>;

export const CARE_MAIN_HOME_STATUSES = [
  'counted',
  'disregarded_occupied',
  'disregarded_lives_there',
  'twelve_week_disregard',
  'sold',
] as const;
export const CareMainHomeStatus = z.enum(CARE_MAIN_HOME_STATUSES);
export type CareMainHomeStatus = z.infer<typeof CareMainHomeStatus>;
export const CARE_MAIN_HOME_STATUS_LABELS: Record<CareMainHomeStatus, string> = {
  counted: 'Counted',
  disregarded_occupied: 'Not counted: someone who qualifies lives there',
  disregarded_lives_there: 'Not counted: still lived in',
  twelve_week_disregard: 'Not counted yet: 12-week disregard',
  sold: 'Sold',
};

export const CareCapitalSummary = z.object({
  /** live = from Money accounts now; snapshot = last figures saved by someone who can see Money; none = nothing recorded. */
  source: z.enum(['live', 'snapshot', 'none']),
  asOf: IsoDate,
  /** When the account figures were last refreshed (snapshot only). */
  snapshotAt: z.string().nullable(),
  /** True when the viewer cannot see Money, so account-level detail is hidden. */
  accountsHidden: z.boolean(),
  assessableCapitalPence: PositivePence,
  /** Assessable capital without the home (the "liquid" capital the projection starts from). */
  liquidCapitalPence: PositivePence,
  lines: z.array(CareCapitalLine),
  groups: z.record(CareCapitalGroup, Pence),
  mainHome: z
    .object({
      status: CareMainHomeStatus,
      valuePence: PositivePence,
      countedPence: PositivePence,
      disregardEndsOn: IsoDate.nullable(),
    })
    .nullable(),
  explanations: z.array(CareExplanation),
});
export type CareCapitalSummary = z.infer<typeof CareCapitalSummary>;

// ---------------------------------------------------------------- income (FR-5.2)

export const CARE_INCOME_KINDS = [
  'state_pension',
  'private_pension',
  'attendance_allowance',
  'pension_credit',
  'other_benefit',
  'other',
] as const;
export const CareIncomeKind = z.enum(CARE_INCOME_KINDS);
export type CareIncomeKind = z.infer<typeof CareIncomeKind>;
export const CARE_INCOME_KIND_LABELS: Record<CareIncomeKind, string> = {
  state_pension: 'State Pension',
  private_pension: 'Private or workplace pension',
  attendance_allowance: 'Attendance Allowance (or Pension Age Disability Payment)',
  pension_credit: 'Pension Credit',
  other_benefit: 'Other benefit',
  other: 'Other income',
};

export const CARE_INCOME_FREQUENCIES = ['weekly', 'four_weekly', 'monthly', 'quarterly', 'annually'] as const;
export const CareIncomeFrequency = z.enum(CARE_INCOME_FREQUENCIES);
export type CareIncomeFrequency = z.infer<typeof CareIncomeFrequency>;
export const CARE_INCOME_FREQUENCY_LABELS: Record<CareIncomeFrequency, string> = {
  weekly: 'Every week',
  four_weekly: 'Every 4 weeks',
  monthly: 'Every month',
  quarterly: 'Every 3 months',
  annually: 'Every year',
};

export const CareIncomeItemDto = z.object({
  id: Id,
  kind: CareIncomeKind,
  description: z.string().nullable(),
  amountPence: PositivePence,
  frequency: CareIncomeFrequency,
  weeklyPence: PositivePence,
  monthlyPence: PositivePence,
});
export type CareIncomeItemDto = z.infer<typeof CareIncomeItemDto>;

export const CreateCareIncomeItemRequest = z.object({
  kind: CareIncomeKind,
  description: optionalText(200),
  amountPence: PositivePence,
  frequency: CareIncomeFrequency,
});
export type CreateCareIncomeItemRequest = z.infer<typeof CreateCareIncomeItemRequest>;
export const UpdateCareIncomeItemRequest = CreateCareIncomeItemRequest.partial();
export type UpdateCareIncomeItemRequest = z.infer<typeof UpdateCareIncomeItemRequest>;

export const CareIncomeSummary = z.object({
  items: z.array(CareIncomeItemDto),
  /** All income, weekly. */
  weeklyTotalPence: PositivePence,
  monthlyTotalPence: PositivePence,
  /** Income counted in the means test after the Attendance Allowance treatment. */
  assessedWeeklyIncomePence: PositivePence,
  aaTreatment: z.enum(['none', 'counted', 'excluded']),
  benefitName: z.string(),
  explanations: z.array(CareExplanation),
});
export type CareIncomeSummary = z.infer<typeof CareIncomeSummary>;

// ---------------------------------------------------------------- fees and funding (FR-5.4, FR-5.10)

export const CareFeesSummary = z.object({
  activeContracts: z.number().int(),
  weeklyFeePence: PositivePence,
  fncWeeklyPence: PositivePence,
  /** Weekly fee less NHS-funded Nursing Care credit. */
  netWeeklyFeePence: PositivePence,
  topUpWeeklyPence: PositivePence,
  nursingWithoutFnc: z.boolean(),
});
export type CareFeesSummary = z.infer<typeof CareFeesSummary>;

export const CARE_FUNDING_POSITIONS = ['self_funding', 'partial', 'council', 'unknown'] as const;
export const CareFundingPosition = z.enum(CARE_FUNDING_POSITIONS);
export type CareFundingPosition = z.infer<typeof CareFundingPosition>;
export const CARE_FUNDING_POSITION_LABELS: Record<CareFundingPosition, string> = {
  self_funding: 'Self-funding',
  partial: 'Partial council funding',
  council: 'Council funding',
  unknown: 'Not worked out yet',
};

export const CareFundingDto = z.object({
  position: CareFundingPosition,
  setting: CareMeansSetting,
  capitalPence: PositivePence,
  limits: z.object({ upperPence: PositivePence, lowerPence: PositivePence }),
  tariffWeeklyPence: PositivePence,
  assessedWeeklyIncomePence: PositivePence,
  protectedWeeklyAllowancePence: PositivePence,
  estimatedWeeklyContributionPence: PositivePence,
  estimatedCouncilWeeklyPence: PositivePence,
  weeklyFeePence: PositivePence,
  explanations: z.array(CareExplanation),
});
export type CareFundingDto = z.infer<typeof CareFundingDto>;

// ---------------------------------------------------------------- projection (FR-5.5, FR-5.6, §7.2)

export const CARE_PROJECTION_EVENT_KINDS = ['house_sale', 'top_up', 'dpa', 'other'] as const;
export const CareProjectionEventKind = z.enum(CARE_PROJECTION_EVENT_KINDS);
export type CareProjectionEventKind = z.infer<typeof CareProjectionEventKind>;
export const CARE_PROJECTION_EVENT_KIND_LABELS: Record<CareProjectionEventKind, string> = {
  house_sale: 'House sale',
  top_up: 'Top-up or gift in',
  dpa: 'Deferred payment agreement',
  other: 'Other one-off',
};

export const CareProjectionEvent = z.object({
  kind: CareProjectionEventKind,
  date: IsoDate,
  /** Positive = money in (house sale, top-up); negative = money out. */
  amountPence: Pence,
  description: z.string().trim().max(120).default(''),
});
export type CareProjectionEvent = z.infer<typeof CareProjectionEvent>;

export const CareProjectionAssumptions = z.object({
  startDate: IsoDate,
  /** Capital at the start, not counting the home (the home is added month by month when included). */
  capitalPence: PositivePence,
  weeklyFeePence: PositivePence,
  upliftPct: z.number().min(0, 'Use 0% or more').max(25, 'Use 25% or less'),
  upliftMonth: z.number().int().min(1).max(12),
  monthlyIncomePence: PositivePence,
  monthlyOtherExpensesPence: PositivePence,
  /** Count the main home month by month under the means-test rules. */
  includeHome: z.boolean(),
  setting: CareMeansSetting,
  events: z.array(CareProjectionEvent).max(20),
  months: z.number().int().min(12).max(120),
});
export type CareProjectionAssumptions = z.infer<typeof CareProjectionAssumptions>;

export const SaveCareProjectionRequest = CareProjectionAssumptions;
export type SaveCareProjectionRequest = z.infer<typeof SaveCareProjectionRequest>;

export const CareProjectionMonth = z.object({
  index: z.number().int(),
  month: CareYearMonth,
  openingPence: Pence,
  incomePence: Pence,
  feesPence: Pence,
  weeklyFeePence: Pence,
  otherPence: Pence,
  eventsPence: Pence,
  closingPence: Pence,
  propertyCountedPence: Pence,
  assessablePence: Pence,
  tariffWeeklyPence: Pence,
});
export type CareProjectionMonth = z.infer<typeof CareProjectionMonth>;



export const CareThresholdAlertDto = z.object({
  kind: z.enum(['lead', 'crossing']),
  leadMonths: z.number().int(),
  month: CareYearMonth,
  date: IsoDate,
  overdue: z.boolean(),
  deadlineStatus: DeadlineStatus.nullable(),
});
export type CareThresholdAlertDto = z.infer<typeof CareThresholdAlertDto>;

export const CareProjectionDto = z.object({
  id: Id,
  computedAt: z.string(),
  ruleSetVersion: z.string().nullable(),
  assumptions: CareProjectionAssumptions,
  series: z.array(CareProjectionMonth),
  upperThresholdPence: PositivePence,
  lowerThresholdPence: PositivePence,
  upperThresholdMonth: CareYearMonth.nullable(),
  upperThresholdMonthIndex: z.number().int().nullable(),
  lowerThresholdMonth: CareYearMonth.nullable(),
  lowerThresholdMonthIndex: z.number().int().nullable(),
  alerts: z.array(CareThresholdAlertDto),
  explanations: z.array(CareExplanation),
});
export type CareProjectionDto = z.infer<typeof CareProjectionDto>;

export const CareProjectionHistoryItem = z.object({
  id: Id,
  computedAt: z.string(),
  ruleSetVersion: z.string().nullable(),
  upperThresholdMonth: CareYearMonth.nullable(),
  lowerThresholdMonth: CareYearMonth.nullable(),
});
export type CareProjectionHistoryItem = z.infer<typeof CareProjectionHistoryItem>;

/** Main-home input for the rules engine (`MainHomeInput`), as computed by the API from the care profile. */
export const CareMainHomeInput = z.object({
  valuePence: PositivePence,
  securedDebtPence: PositivePence,
  disregardReason: CareHomeDisregard.exclude(['none']).nullable(),
  permanentResidentialCare: z.boolean(),
  admissionDate: IsoDate.nullable(),
  saleDate: IsoDate.nullable(),
});
export type CareMainHomeInput = z.infer<typeof CareMainHomeInput>;

/**
 * Maps saved assumptions to the rules engine input (`ProjectCareCapitalInput` minus `ruleSet`).
 * Used by both the API (persisted projection) and the web (instant client-side recompute, §13.2).
 */
export function careProjectionInputFrom(
  a: CareProjectionAssumptions,
  home: CareMainHomeInput | null,
  leadMonths: readonly number[] = [6, 3, 1],
) {
  return {
    startDate: a.startDate,
    capitalPence: a.capitalPence,
    weeklyFeePence: a.weeklyFeePence,
    upliftPct: a.upliftPct,
    upliftMonth: a.upliftMonth,
    monthlyIncomePence: a.monthlyIncomePence,
    monthlyOtherExpensesPence: a.monthlyOtherExpensesPence,
    events: a.events.map((e) => ({
      date: e.date,
      amountPence: e.amountPence,
      description: e.description || CARE_PROJECTION_EVENT_KIND_LABELS[e.kind],
    })),
    property: a.includeHome && home ? home : null,
    setting: a.setting,
    months: a.months,
    leadMonths: [...leadMonths],
  };
}

// ---------------------------------------------------------------- overview (Money › Care)

export const CareOverview = z.object({
  asOf: IsoDate,
  nation: z.string(),
  ruleSetVersion: z.string(),
  partialRules: z.array(z.string()),
  canSeeMoney: z.boolean(),
  profile: CareProfileDto,
  /** Accounts that could be the main home (type property) — only when the viewer can see Money. */
  homeAccountOptions: z.array(z.object({ id: Id, label: z.string(), valuePence: Pence.nullable(), asOf: IsoDate.nullable() })),
  capital: CareCapitalSummary,
  capitalItems: z.array(CareCapitalItemDto),
  income: CareIncomeSummary,
  fees: CareFeesSummary,
  funding: CareFundingDto,
  /** FR-5.10: nursing home and no NHS-funded Nursing Care recorded. */
  fncPrompt: z.boolean(),
  homeInput: CareMainHomeInput.nullable(),
  suggestedAssumptions: CareProjectionAssumptions,
  latestProjection: CareProjectionDto.nullable(),
  firstCouncilContactDate: IsoDate.nullable(),
  lastCouncilContactDate: IsoDate.nullable(),
});
export type CareOverview = z.infer<typeof CareOverview>;

// ---------------------------------------------------------------- care events timeline (FR-5.7, FR-5.9, FR-5.10)

export const CARE_EVENT_TYPES = [
  'council_contact',
  'needs_assessment',
  'financial_assessment',
  'council_decision',
  'dpa',
  'property_disregard',
  'fnc_awarded',
  'chc_decision',
  'review',
  'appeal',
  'admission',
  'other',
] as const;
export const CareEventType = z.enum(CARE_EVENT_TYPES);
export type CareEventType = z.infer<typeof CareEventType>;
export const CARE_EVENT_TYPE_LABELS: Record<CareEventType, string> = {
  council_contact: 'Contact with the council',
  needs_assessment: 'Care needs assessment',
  financial_assessment: 'Financial assessment',
  council_decision: 'Council decision',
  dpa: 'Deferred payment agreement',
  property_disregard: '12-week property disregard',
  fnc_awarded: 'NHS-funded Nursing Care awarded',
  chc_decision: 'NHS Continuing Healthcare decision',
  review: 'Review',
  appeal: 'Appeal',
  admission: 'Moved into care',
  other: 'Other event',
};

export const CARE_CONTACT_METHODS = ['phone', 'email', 'letter', 'online_form', 'in_person', 'other'] as const;
export const CareContactMethod = z.enum(CARE_CONTACT_METHODS);
export type CareContactMethod = z.infer<typeof CareContactMethod>;
export const CARE_CONTACT_METHOD_LABELS: Record<CareContactMethod, string> = {
  phone: 'Phone call',
  email: 'Email',
  letter: 'Letter',
  online_form: 'Online form',
  in_person: 'In person',
  other: 'Other',
};

export const CareEventDto = z.object({
  id: Id,
  type: CareEventType,
  date: IsoDate,
  endDate: IsoDate.nullable(),
  outcome: z.string().nullable(),
  amountPence: Pence.nullable(),
  method: CareContactMethod.nullable(),
  contactName: z.string().nullable(),
  reference: z.string().nullable(),
  notes: z.string().nullable(),
  /** 'health' = read-only mirror of the CHC process (health module). */
  source: z.enum(['care', 'health']),
  readOnly: z.boolean(),
  createdAt: z.string().nullable(),
});
export type CareEventDto = z.infer<typeof CareEventDto>;

export const CreateCareEventRequest = z.object({
  /** Optional client-generated id for idempotent offline creates (ADR 0004). */
  id: Id.optional(),
  type: CareEventType,
  date: IsoDate,
  endDate: IsoDate.nullish(),
  outcome: optionalText(300),
  amountPence: Pence.nullish(),
  method: CareContactMethod.nullish(),
  contactName: optionalText(200),
  reference: optionalText(100),
  notes: optionalText(5000),
});
export type CreateCareEventRequest = z.infer<typeof CreateCareEventRequest>;
export const UpdateCareEventRequest = CreateCareEventRequest.omit({ id: true }).partial();
export type UpdateCareEventRequest = z.infer<typeof UpdateCareEventRequest>;

export const CareTimelineDto = z.object({
  items: z.array(CareEventDto),
  firstCouncilContactDate: IsoDate.nullable(),
  /** True when the CHC decision mirror could be read (health visible to the viewer). */
  chcMirrored: z.boolean(),
});
export type CareTimelineDto = z.infer<typeof CareTimelineDto>;

// ---------------------------------------------------------------- contracts, top-ups, invoices (FR-5.8, FR-5.10)

export const CARE_CONTRACT_SETTINGS = ['residential', 'nursing', 'home_care', 'other'] as const;
export const CareContractSetting = z.enum(CARE_CONTRACT_SETTINGS);
export type CareContractSetting = z.infer<typeof CareContractSetting>;
export const CARE_CONTRACT_SETTING_LABELS: Record<CareContractSetting, string> = {
  residential: 'Residential care home',
  nursing: 'Nursing home',
  home_care: 'Care at home',
  other: 'Other',
};

/** 'contract' (anniversary of the start date), 'april', or a fixed `MM-DD`. */
export const CareUpliftAnniversary = z
  .string()
  .regex(/^(contract|april|(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/, 'Choose when fees usually go up');
export type CareUpliftAnniversary = z.infer<typeof CareUpliftAnniversary>;

/** Month (1–12) in which fees go up, from the contract's uplift anniversary. */
export function careUpliftMonthFor(anniversary: string | null | undefined, startDate: string): number {
  if (anniversary === 'april') return 4;
  if (anniversary && /^\d{2}-\d{2}$/.test(anniversary)) return Number(anniversary.slice(0, 2));
  return Number(startDate.slice(5, 7));
}

export const CareContractDto = z.object({
  id: Id,
  providerName: z.string(),
  setting: CareContractSetting.nullable(),
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  weeklyRatePence: PositivePence,
  upliftPct: z.number(),
  upliftAnniversary: z.string(),
  upliftMonth: z.number().int(),
  inclusions: z.string().nullable(),
  noticePeriod: z.string().nullable(),
  reviewDates: z.array(IsoDate),
  nextReviewDate: IsoDate.nullable(),
  fncWeeklyPence: PositivePence.nullable(),
  fncFrom: IsoDate.nullable(),
  documentId: Id.nullable(),
  notes: z.string().nullable(),
  active: z.boolean(),
  invoiceCount: z.number().int(),
  unpaidCount: z.number().int(),
  overdueCount: z.number().int(),
  unpaidPence: PositivePence,
  topUpWeeklyPence: PositivePence,
});
export type CareContractDto = z.infer<typeof CareContractDto>;

const ContractFields = z.object({
  providerName: z.string().trim().min(1, 'Enter the care provider’s name').max(200),
  setting: CareContractSetting,
  startDate: IsoDate,
  endDate: IsoDate.nullish(),
  weeklyRatePence: PositivePence,
  upliftPct: z.number().min(0).max(25),
  upliftAnniversary: CareUpliftAnniversary,
  inclusions: optionalText(2000),
  noticePeriod: optionalText(200),
  reviewDates: z.array(IsoDate).max(12),
  fncWeeklyPence: PositivePence.nullish(),
  fncFrom: IsoDate.nullish(),
  documentId: Id.nullish(),
  notes: optionalText(5000),
});
const contractDatesOk = (c: { startDate?: string; endDate?: string | null }) =>
  !c.startDate || !c.endDate || c.endDate >= c.startDate;
export const CreateCareContractRequest = ContractFields.extend({
  upliftPct: z.number().min(0).max(25).optional(),
  upliftAnniversary: CareUpliftAnniversary.optional(),
  reviewDates: z.array(IsoDate).max(12).optional(),
}).refine(contractDatesOk, { message: 'The end date must be on or after the start date', path: ['endDate'] });
export type CreateCareContractRequest = z.infer<typeof CreateCareContractRequest>;
export const UpdateCareContractRequest = ContractFields.partial().refine(contractDatesOk, {
  message: 'The end date must be on or after the start date',
  path: ['endDate'],
});
export type UpdateCareContractRequest = z.infer<typeof UpdateCareContractRequest>;

export const CareTopUpDto = z.object({
  id: Id,
  contractId: Id.nullable(),
  payerName: z.string(),
  relationship: z.string().nullable(),
  payerUserId: Id.nullable(),
  weeklyAmountPence: PositivePence,
  agreedInWriting: z.boolean(),
  startDate: IsoDate.nullable(),
  endDate: IsoDate.nullable(),
  notes: z.string().nullable(),
  documentId: Id.nullable(),
  active: z.boolean(),
});
export type CareTopUpDto = z.infer<typeof CareTopUpDto>;

const CareTopUpFields = z.object({
  contractId: Id.nullish(),
  payerName: z.string().trim().min(1, 'Enter who pays the top-up').max(200),
  relationship: optionalText(100),
  payerUserId: Id.nullish(),
  weeklyAmountPence: PositivePence,
  agreedInWriting: z.boolean(),
  startDate: IsoDate.nullish(),
  endDate: IsoDate.nullish(),
  notes: optionalText(2000),
  documentId: Id.nullish(),
});
export const CreateCareTopUpRequest = CareTopUpFields.extend({ agreedInWriting: z.boolean().default(false) });
export type CreateCareTopUpRequest = z.infer<typeof CreateCareTopUpRequest>;
export const UpdateCareTopUpRequest = CareTopUpFields.partial();
export type UpdateCareTopUpRequest = z.infer<typeof UpdateCareTopUpRequest>;

export const CARE_INVOICE_STATUSES = ['unpaid', 'paid', 'disputed'] as const;
export const CareInvoiceStatus = z.enum(CARE_INVOICE_STATUSES);
export type CareInvoiceStatus = z.infer<typeof CareInvoiceStatus>;
export const CARE_INVOICE_STATUS_LABELS: Record<CareInvoiceStatus, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
  disputed: 'Disputed',
};

export const CARE_PAYER_TYPES = ['principal', 'top_up', 'council', 'nhs'] as const;
export const CarePayerType = z.enum(CARE_PAYER_TYPES);
export type CarePayerType = z.infer<typeof CarePayerType>;
export const CARE_PAYER_TYPE_LABELS: Record<CarePayerType, string> = {
  principal: 'Paid from their own money',
  top_up: 'Top-up by someone else',
  council: 'Council',
  nhs: 'NHS',
};

export const CareInvoiceDto = z.object({
  id: Id,
  contractId: Id,
  providerName: z.string(),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  /** Amount invoiced before any NHS-funded Nursing Care credit. */
  amountPence: PositivePence,
  fncCreditPence: PositivePence,
  /** amount − FNC credit. */
  payablePence: PositivePence,
  dueDate: IsoDate.nullable(),
  paidOn: IsoDate.nullable(),
  status: CareInvoiceStatus,
  payerType: CarePayerType,
  topUpPayerId: Id.nullable(),
  topUpPayerName: z.string().nullable(),
  reference: z.string().nullable(),
  notes: z.string().nullable(),
  documentId: Id.nullable(),
  overdue: z.boolean(),
});
export type CareInvoiceDto = z.infer<typeof CareInvoiceDto>;

const InvoiceFields = z.object({
  contractId: Id,
  periodStart: IsoDate,
  periodEnd: IsoDate,
  amountPence: PositivePence,
  fncCreditPence: PositivePence,
  dueDate: IsoDate.nullish(),
  paidOn: IsoDate.nullish(),
  status: CareInvoiceStatus,
  payerType: CarePayerType,
  topUpPayerId: Id.nullish(),
  reference: optionalText(100),
  notes: optionalText(2000),
  documentId: Id.nullish(),
});
const invoiceOk = (v: Partial<z.infer<typeof InvoiceFields>>) =>
  (!v.periodStart || !v.periodEnd || v.periodEnd >= v.periodStart) &&
  (v.amountPence === undefined || v.fncCreditPence === undefined || v.fncCreditPence <= v.amountPence);
const invoiceIssue = {
  message: 'Check the dates and amounts: the period must end after it starts and the FNC credit cannot be more than the invoice',
};
export const CreateCareInvoiceRequest = InvoiceFields.extend({
  /** Optional client-generated id (idempotent create). */
  id: Id.optional(),
  fncCreditPence: PositivePence.default(0),
  status: CareInvoiceStatus.default('unpaid'),
  payerType: CarePayerType.default('principal'),
}).refine(invoiceOk, invoiceIssue);
export type CreateCareInvoiceRequest = z.input<typeof CreateCareInvoiceRequest>;
export const UpdateCareInvoiceRequest = InvoiceFields.partial().refine(invoiceOk, invoiceIssue);
export type UpdateCareInvoiceRequest = z.infer<typeof UpdateCareInvoiceRequest>;

export const CareInvoiceListQuery = z.object({
  contractId: Id.optional(),
  status: z.enum(['unpaid', 'paid', 'disputed', 'overdue']).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});
export type CareInvoiceListQuery = z.infer<typeof CareInvoiceListQuery>;

export const CareInvoiceListResponse = z.object({
  items: z.array(CareInvoiceDto),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  totals: z.object({
    amountPence: PositivePence,
    fncCreditPence: PositivePence,
    payablePence: PositivePence,
    unpaidPence: PositivePence,
    overdueCount: z.number().int(),
  }),
});
export type CareInvoiceListResponse = z.infer<typeof CareInvoiceListResponse>;

// ---------------------------------------------------------------- exports (FR-5.11) + Home summary (ARCHITECTURE §7)

export const CARE_EXPORT_KINDS = ['capital-statement', 'fee-history', 'timeline'] as const;
export const CareExportKind = z.enum(CARE_EXPORT_KINDS);
export type CareExportKind = z.infer<typeof CareExportKind>;
export const CareExportRequest = z.object({
  format: z.enum(['pdf', 'csv']).default('pdf'),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export type CareExportRequest = z.input<typeof CareExportRequest>;

export const CareSummaryResponse = z.union([
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    fundingPosition: CareFundingPosition,
    monthsToUpperThreshold: z.number().int().nullable(),
    /** `YYYY-MM` of the projected upper-threshold month (latest saved projection). */
    upperThresholdMonth: CareYearMonth.nullable(),
    lowerThresholdMonth: CareYearMonth.nullable(),
    assessableCapitalPence: PositivePence,
  }),
]);
export type CareSummaryResponse = z.infer<typeof CareSummaryResponse>;
